import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
    Box,
    Badge,
    Typography,
    Link,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemText
} from "@mui/material/";
import MessageSharpIcon from "@mui/icons-material/MessageSharp";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { logToSentry } from "../../../../config/Sentry";
import actions from "../../../../Store/actions";
import { strings } from "../../../../common/i18n";
import { format } from "../../../../common/lib";
import styles from "./styles";

type Props = {
    loading: boolean
};

/**
 * Audit Comments Component
 */

export default ({ loading }: Props) => {
    const dispatch = useDispatch();
    const {
        audits,
        auth,
        accounts,
        auditComment: auditCommentsState
    } = useSelector((state) => state);

    const commentData = auditCommentsState?.data ?? [];
    const accountData = accounts?.data || [];
    const auditId = audits?.item?.id;
    const currentUserId = auth?.account?.id;

    const [open, setOpen] = useState(false);
    const [tempComment, setTempComment] = useState("");
    const [editMode, setEditMode] = useState(false);

    // Get the latest comment
    const latestComment = useMemo(
        () =>
            commentData.length > 0
                ? [...commentData].sort(
                      (a, b) =>
                          new Date(b.created_date) - new Date(a.created_date)
                  )[0]
                : null,
        [commentData]
    );

    useEffect(() => {
        if (auditId) {
            dispatch(actions.auditComments.create.getRequest(auditId));
        }
    }, [auditId, dispatch]);

    //opening dialog for add or edit
    const onOpenDialog = useCallback(() => {
        setTempComment(latestComment?.content || "");
        setEditMode(!!latestComment);
        setOpen(true);
    }, [latestComment]);

    const onCloseDialog = useCallback(() => {
        setTempComment("");
        setEditMode(false);
        setOpen(false);
    }, []);

    const onSave = useCallback(async () => {
        const content = tempComment.trim();
        if (!content) return;

        const timestamp = new Date().toISOString();

        const payload = {
            audit: Number(auditId),
            content,
            created_by: currentUserId,
            created_date: timestamp,
            updated_by: currentUserId,
            updated_date: timestamp
        };

        try {
            if (editMode && latestComment?.id) {
                const patchPayload = {
                    ...payload,
                    created_by: latestComment.created_by,
                    created_date: latestComment.created_date
                };
                await dispatch(
                    actions.auditComments.create.patchRequest(
                        latestComment.id,
                        patchPayload,
                        auditId
                    )
                );
                dispatch(
                    actions.toast.create.success(
                        strings("messages.commentUpdated")
                    )
                );
            } else {
                await dispatch(
                    actions.auditComments.create.postRequest(payload)
                );
                dispatch(
                    actions.toast.create.success(
                        strings("messages.commentAdded")
                    )
                );
            }
            await dispatch(actions.auditComments.create.getRequest(auditId));
            onCloseDialog();
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On saving comment:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.commentSaveFailed"))
            );
        }
    }, [
        tempComment,
        editMode,
        latestComment,
        auditId,
        currentUserId,
        dispatch,
        onCloseDialog
    ]);

    const metadataText = useMemo(() => {
        if (!latestComment) return "";
        const createdBy =
            accountData.find(
                (account) => account.id === latestComment.created_by
            )?.name || "";
        if (latestComment.updated_by && latestComment.updated_date) {
            const updatedBy =
                accountData.find(
                    (account) => account.id === latestComment.updated_by
                )?.name || "";
            return `${format.dateTimeNew(
                latestComment.updated_date
            )} • ${updatedBy} (edited)`;
        }
        return `${format.dateTimeNew(
            latestComment.created_date
        )} • ${createdBy}`;
    }, [latestComment, accountData]);

    return (
        <>
            <Box sx={styles.mainHeaderContainer}>
                <Badge badgeContent={0} color="praimry">
                    <MessageSharpIcon color="primary" />
                    <Typography variant="body1" sx={{ ml: 1 }}>
                        {strings("auditComment")}
                    </Typography>
                </Badge>

                <Link
                    component="button"
                    variant="caption"
                    onClick={onOpenDialog}
                >
                    {latestComment ? strings("edit") : strings("add")}
                </Link>
                <Box />
            </Box>

            {/* mainHeaderContainer above lays its title out with floats, so
                it contributes no height of its own. clear:both drops this list
                below them but leaves the two touching — hence the explicit
                gap. Scoped here rather than on the shared auditorList style,
                which the Auditors list also uses. */}
            <List sx={{ ...styles.auditorList, mt: 1 }}>
                <ListItem sx={styles.listItem}>
                    <ListItemText
                        primary={
                            <Typography
                                variant="caption"
                                sx={{ textTransform: "none !important" }}
                            >
                                {latestComment?.content ||
                                    strings("pleaseAddComment")}
                            </Typography>
                        }
                        secondary={
                            metadataText && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ textTransform: "none" }}
                                >
                                    {metadataText}
                                </Typography>
                            )
                        }
                    />
                </ListItem>
            </List>

            <Dialog fullWidth maxWidth="sm" open={open} onClose={onCloseDialog}>
                <DialogTitle sx={styles.auditCommentDialogTitle}>
                    {editMode
                        ? strings("editAuditComment")
                        : strings("addAuditComment")}
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={onCloseDialog}
                    sx={(theme) => styles.auditCommentCloseButton(theme)}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent dividers>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        value={tempComment}
                        onChange={(e) => setTempComment(e.target.value)}
                    />
                    <Box
                        sx={{
                            mt: 2,
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1
                        }}
                    >
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onCloseDialog}
                        >
                            {strings("cancel")}
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={onSave}
                            disabled={!tempComment.trim()}
                        >
                            {strings("save")}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};
